import React from 'react';

import { AwardJapanStandard } from '@travel/icons/award';
import { NonRefundable } from '@travel/icons/service';
import { Spot } from '@travel/icons/ui';
import JapanStandardPopup from '@travel/traveler-core/components/JapanStandardPopup';
import RakutenAwardPopup from '@travel/traveler-core/components/RakutenAwardPopup';
import { useDialogHandler, useTranslation } from '@travel/traveler-core/hooks';
import { IconText, IconTextList, RakutenAward, Tag, Tooltip } from '@travel/ui';
import { cx, isNotEmptyArray } from '@travel/utils';

import DataLayer from 'components/DataLayer';
import MembersOnlyTag from 'components/MembersOnlyTag';
import NearbyTerminals from 'components/NearbyTerminals';
import { mappedIconTextListData } from 'pages/ProviderInformationPage/helpers';

import { RAT_PAGE_TYPE_OBJ } from 'constants/ratAnalytics';
import { Translate } from 'core/translate';
import type { ProviderItem } from 'ProviderList-Types';

import styles from './providerBox.module.scss';

type Props = {
  /** on what parent page this component lives */
  onPage: 'providerListUndated' | 'providerListDated';
  /** Object of provider data */
  item: ProviderItem;
  /** Type to define the current screen type */
  deviceType: string;
};

function ProviderDetailsBox(props: Props) {
  const { onPage, item, deviceType } = props;
  const { parentName, siteSection, pageType } = RAT_PAGE_TYPE_OBJ[onPage];

  const features = mappedIconTextListData(item.features, styles.featureIcon);

  const rakutenAward = isNotEmptyArray(item.certifications)
    ? item.certifications
        .filter((award) => award.image)
        .map((award) => (
          <RakutenAward
            key={award?.name}
            title={award?.name}
            image={award?.image}
            imageClassName={styles.awardImage}
            data-testid={`providerDetailBox-award-${award?.name}`}
          />
        ))[0]
    : null;

  const {
    isOpen: isJapanStandardPopupOpen,
    onOpen: onJapanStandardPopupOpen,
    onClose: onJapanStandardPopupClose,
  } = useDialogHandler(false);
  const {
    isOpen: isAwardsPopupOpen,
    onOpen: onAwardsPopupOpen,
    onClose: onAwardsPopupClose,
  } = useDialogHandler(false);
  const {
    isOpen: isAwardsTooltipOpen,
    onOpen: onAwardsTooltipOpen,
    onClose: onAwardsTooltipClose,
  } = useDialogHandler(false);
  const {
    isOpen: isTooltipJapanStandardOpen,
    onOpen: onTooltipJapanStandardOpen,
    onClose: onTooltipJapanStandardClose,
  } = useDialogHandler(false);

  const japanStandardPopupAriaLabel = useTranslation({ id: 'Traveler_Common.Japan_Quality.Name' });

  const handleBubbling = (event: React.MouseEvent) => {
    event.stopPropagation();
    event.preventDefault();
  };
  const handlePropagation = (event: React.KeyboardEvent) => {
    if (event.key === ' ' || event.key === 'Enter') {
      event.stopPropagation();
    }
  };
  const handleJapanStandardPopupOpen = (event: React.MouseEvent) => {
    handleBubbling(event);
    onJapanStandardPopupOpen();
  };
  const handleAwardsPopupOpen = (event: React.MouseEvent) => {
    handleBubbling(event);
    onAwardsPopupOpen();
  };

  return (
    <div className={styles.itemAmenitiesAndInfo} data-testid="providerDetailBox-wrapper">
      {isJapanStandardPopupOpen && (
        <button
          onClick={handleBubbling}
          onKeyDown={handlePropagation}
          className={styles.japanStandardBtn}
        >
          <JapanStandardPopup
            isOpen={isJapanStandardPopupOpen}
            onCloseCallback={onJapanStandardPopupClose}
          />
          <DataLayer
            pageName={`${parentName}:japan_quality_information_popup`}
            pageType={pageType}
            siteSection={siteSection}
          />
        </button>
      )}
      {isAwardsPopupOpen && (
        <button
          onClick={handleBubbling}
          onKeyDown={handlePropagation}
          className={styles.japanStandardBtn}
        >
          <RakutenAwardPopup isOpen={isAwardsPopupOpen} onClose={onAwardsPopupClose} />
          <DataLayer
            pageName={`${parentName}:rakuten_award_information_popup`}
            pageType={pageType}
            siteSection={siteSection}
          />
        </button>
      )}
      <div className={styles.awardsContainer}>
        {item.isJapanStandard && (
          <button
            className={styles.japanStandardAward}
            data-testid="providerDetailBox-japanStandardAward"
            aria-label={japanStandardPopupAriaLabel}
            onClick={handleJapanStandardPopupOpen}
            onKeyDown={handlePropagation}
            onMouseEnter={onTooltipJapanStandardOpen}
            onMouseLeave={onTooltipJapanStandardClose}
            onFocus={onTooltipJapanStandardOpen}
            onBlur={onTooltipJapanStandardClose}
          >
            <AwardJapanStandard className={styles.award} />
            {isTooltipJapanStandardOpen && (
              <Tooltip className={cx(styles.toolTip, styles.japanQualityToolTipPosition)}>
                <Translate id="Traveler_Common.Japan_Quality.Hover" />
              </Tooltip>
            )}
          </button>
        )}
        {isNotEmptyArray(item.certifications) && (
          <button
            className={styles.japanStandardAward}
            data-testid="providerDetailBox-rakutenAward"
            onClick={handleAwardsPopupOpen}
            onKeyDown={handlePropagation}
            onMouseEnter={onAwardsTooltipOpen}
            onMouseLeave={onAwardsTooltipClose}
            onFocus={onAwardsTooltipOpen}
            onBlur={onAwardsTooltipClose}
          >
            {rakutenAward}
            {isAwardsTooltipOpen && (
              <Tooltip className={cx(styles.toolTip, styles.rakutenAwardToolTipPosition)}>
                <Translate id="Traveler_Common.Awards.Hover" />
              </Tooltip>
            )}
          </button>
        )}
      </div>

      <div className={styles.tagsContainer}>
        {item.hasMemberOnly && (
          <MembersOnlyTag text={<Translate id="Provider_List.Search.Members_only_tag" />} />
        )}

        {item.freeUntil && (
          <Tag
            type="outlined"
            color="green"
            className={styles.providerTag}
            data-testid="providerDetailBox-freeCancellation-tag"
          >
            <Translate id="Cancellation_Policy_Common.Provider_List.Free_Cancellation" />
          </Tag>
        )}

        {item.nonRefundable && (
          <IconText
            className={styles.nonRefundableContainer}
            textClassName={styles.nonRefundable}
            icon={<NonRefundable size={12} />}
            text={<Translate id="Common_Price_Details.Out_To_Out.Payment.Non_Refundable" />}
            spaceBetween={4}
            data-testid="providerDetailBox-nonrefundable-tag"
          />
        )}
      </div>

      <div className={styles.featuresContainer}>
        {isNotEmptyArray(features) && (
          <IconTextList
            className={styles.featuresList}
            items={features.slice(0, 3)}
            iconTextSpacing={4}
            iconTextClassName={styles.textStyle}
          />
        )}
      </div>

      {deviceType !== 'sp' && item.nearbyTerminal && (
        <IconText
          className={cx(styles.textStyle, styles.iconTextStyle)}
          icon={<Spot size={16} />}
          text={<NearbyTerminals {...item.nearbyTerminal} />}
          spaceBetween={4}
        />
      )}
    </div>
  );
}

export default ProviderDetailsBox;
